import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as XLSX from 'xlsx';
import { Download } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export function MedicalSuggestionsUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.schoolId) {
      toast({
        title: "Error",
        description: "Please select a file and ensure you're logged in",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Transform the data into the format we need
      const suggestions = [];
      
      // Process complaints
      for (const row of jsonData as any[]) {
        if (row.COMPLAINT) {
          suggestions.push({
            type: 'COMPLAINT',
            suggestion_text: row.COMPLAINT,
            school_id: user.schoolId,
          });
        }
        if (row.DIAGNOSIS) {
          suggestions.push({
            type: 'DIAGNOSIS',
            suggestion_text: row.DIAGNOSIS,
            school_id: user.schoolId,
          });
        }
        if (row.TREATMENT) {
          suggestions.push({
            type: 'TREATMENT',
            suggestion_text: row.TREATMENT,
            school_id: user.schoolId,
          });
        }
      }

      if (suggestions.length === 0) {
        throw new Error('No valid suggestions found in the Excel file');
      }

      // Insert suggestions one by one, handling duplicates
      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;
      const totalSuggestions = suggestions.length;

      for (const [index, suggestion] of suggestions.entries()) {
        try {
          // Update progress
          setProgress(Math.round((index / totalSuggestions) * 100));

          // Check if suggestion already exists
          const { data: existingData, error: checkError } = await supabase
            .from('medical_suggestions')
            .select('id')
            .eq('type', suggestion.type)
            .eq('suggestion_text', suggestion.suggestion_text)
            .eq('school_id', suggestion.school_id)
            .maybeSingle();

          if (checkError) {
            console.error('Error checking suggestion:', checkError);
            errorCount++;
            continue;
          }

          if (!existingData) {
            // Insert new suggestion if it doesn't exist
            const { error: insertError } = await supabase
              .from('medical_suggestions')
              .insert([suggestion]);

            if (insertError) {
              console.error('Error inserting suggestion:', insertError);
              errorCount++;
              continue;
            }
            successCount++;
          } else {
            skipCount++;
          }
        } catch (error) {
          console.error('Error processing suggestion:', error);
          errorCount++;
        }
      }

      // Set final progress
      setProgress(100);

      const message = `${successCount} suggestions imported, ${skipCount} duplicates skipped${
        errorCount > 0 ? `, ${errorCount} errors encountered` : ''
      }`;

      toast({
        title: errorCount > 0 ? "Partial Success" : "Success",
        description: message,
        variant: errorCount > 0 ? "destructive" : "default",
      });
    } catch (error) {
      console.error('Error uploading suggestions:', error);
      toast({
        title: "Error",
        description: "Failed to import suggestions. Please check the file format and try again.",
        variant: "destructive",
      });
    } finally {
      // Reset the file input and loading state after a short delay to show 100% progress
      setTimeout(() => {
        setIsUploading(false);
        setProgress(0);
        e.target.value = '';
      }, 500);
    }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['COMPLAINT', 'DIAGNOSIS', 'TREATMENT'],
      ['Headache', 'Migraine', 'Paracetamol'],
      ['', '', ''],
      ['', '', ''],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'medical_suggestions_template.xlsx');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <Input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          disabled={isUploading}
        />
        {isUploading && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground">
              Uploading... {progress}%
            </p>
          </div>
        )}
      </div>
      <Button
        variant="outline"
        onClick={downloadTemplate}
        className="w-full sm:w-auto"
        disabled={isUploading}
      >
        <Download className="mr-2 h-4 w-4" />
        Download Template
      </Button>
      <div className="text-sm text-muted-foreground">
        Upload an Excel file with columns: COMPLAINT, DIAGNOSIS, and TREATMENT. Each row can contain one or more types of medical suggestions.
      </div>
    </div>
  );
}